/**
 * The review after an import (Phase 3f, `document-ai/0017` §9, answered
 * 2026-09-22): marking a bundle reviewed, and the change log in words. Its
 * outline editing went on 2026-10-03 with the outline's section
 * (`document-ai/0032`).
 *
 * Kept apart from `documents-api.ts`, which is the upload machine and already
 * long. The keys and the re-reads are that file's, so a change made here moves
 * every screen that shows where a file sits.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { ProjectChange, ProjectImport } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import { documentKeys, importKeys } from "@/features/project/documents-api";

export const changeKeys = {
  /** Every change, or one bundle's. The project's key prefixes each bundle's. */
  list: (projectId: string, importId?: string) =>
    importId
      ? (["project", projectId, "changes", importId] as const)
      : (["project", projectId, "changes"] as const),
};

/** The path of an import's review screen. */
/** Every open question of the project, on one screen (2026-10-03). */
export function projectReviewPath(projectId: string): string {
  return `/projects/${projectId}/review`;
}

export function reviewPath(projectId: string, importId: string): string {
  return `/projects/${projectId}/imports/${importId}`;
}

/**
 * Re-read everything a review action can move: where files sit, the imports,
 * and the log. The Brain is its own key — see the note in `connectors-api.ts`.
 */
function invalidateReview(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({ queryKey: documentKeys.list(projectId) });
  void queryClient.invalidateQueries({ queryKey: ["project", projectId, "brain"] });
  void queryClient.invalidateQueries({ queryKey: importKeys.list(projectId) });
  void queryClient.invalidateQueries({ queryKey: changeKeys.list(projectId) });
}

/**
 * What has been done to the project's organisation, in words, newest first.
 * Narrowed to one bundle when `importId` is given.
 */
export function useChanges(projectId: string | undefined, importId?: string) {
  return useQuery({
    queryKey: changeKeys.list(projectId ?? "", importId),
    enabled: Boolean(projectId),
    queryFn: async (): Promise<ProjectChange[]> => {
      const res = await api.projects.listChanges({
        params: { id: projectId! },
        query: importId ? { importId } : {},
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.changes;
    },
  });
}

/**
 * A person has been through the bundle.
 *
 * **Invalidated, not polled.** `useImport` stops polling once an import
 * reaches `review`, so waiting for a poll to notice `done` would wait for
 * ever. The banner that offered the review reads the imports list, which this
 * re-reads, so it goes away by itself.
 */
export function useReviewImport(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (importId: string): Promise<ProjectImport> => {
      const res = await api.projects.reviewImport({
        params: { id: projectId, importId },
        body: {},
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.import;
    },
    onSuccess: () => invalidateReview(queryClient, projectId),
  });
}
